/** Monthly / total paper-agent stats cards (Analytics+ style). */

import type { PaperAgentStats } from "../api/agentsPaperClient";
import { renderTargetProgressIndicator } from "./analytics";
import { collapsibleOpenAttr } from "./collapsibleSections";

const AGENTS_COLLAPSIBLE_SCOPE = "agents";

export type PaperAgentTokenContext = {
  marketdata?: {
    configured?: boolean;
    dailyBudget?: number;
    creditsUsedToday?: number;
    creditsRemaining?: number;
  };
  cursor?: {
    configured?: boolean;
    month?: string;
    tokensUsedThisMonth?: number;
    tokensUsedToday?: number;
  };
};

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function fmtUsdWhole(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value < 0 ? "-" : "";
  return `${sign}$${Math.round(Math.abs(value)).toLocaleString("en-US", {
    maximumFractionDigits: 0,
    useGrouping: true,
  })}`;
}

function fmtPct(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  })}%`;
}

function fmtInt(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "0";
  return Math.round(value).toLocaleString("en-US", { useGrouping: true });
}

/** Analytics+ TOTAL INCOME secondary line: right-aligned "Label <b>value</b>". */
function detailLine(label: string, valueHtml: string, extraClass = ""): string {
  const cls = ["sfe-income-kpi-detail", "sfe-income-kpi-detail-bold", extraClass]
    .filter(Boolean)
    .join(" ");
  return `<span class="${cls}">${esc(label)} <b>${valueHtml}</b></span>`;
}

function detailsBlock(lines: string[]): string {
  return `<div class="sfe-income-kpi-details">${lines.join("")}</div>`;
}

function tokenMetricLinesHtml(rows: Array<{ label: string; value: string }>): string {
  const lines = rows
    .map(
      (row) => `<div class="sfe-month-roc-line">
    <span class="sfe-month-roc-line-k">${esc(row.label)}</span>
    <div class="sfe-month-roc-row">
      <span class="kpi-v sfe-month-card-static-val">${row.value}</span>
    </div>
  </div>`,
    )
    .join("");
  return `<div class="sfe-month-roc-lines">${lines}</div>`;
}

function marketDataTokenCardHtml(
  periodTotal: number,
  tokenContext?: PaperAgentTokenContext,
  opts?: { totalLabel?: string },
): string {
  const md = tokenContext?.marketdata;
  const today =
    md?.configured !== false && md?.creditsUsedToday != null ? fmtInt(md.creditsUsedToday) : "—";
  const left =
    md?.configured !== false && md?.creditsRemaining != null ? fmtInt(md.creditsRemaining) : "—";
  return tokenMetricLinesHtml([
    { label: "Today", value: esc(today) },
    { label: opts?.totalLabel ?? "Total (period)", value: esc(fmtInt(periodTotal)) },
    { label: "Left", value: esc(left) },
  ]);
}

function claudeTokenCardHtml(
  periodTotal: number,
  tokenContext?: PaperAgentTokenContext,
  opts?: { totalLabel?: string; todayClaude?: number | null },
): string {
  const cursor = tokenContext?.cursor;
  const todayValue =
    opts?.todayClaude != null
      ? opts.todayClaude
      : cursor?.configured !== false && cursor?.tokensUsedToday != null
        ? cursor.tokensUsedToday
        : null;
  const today = todayValue != null ? fmtInt(todayValue) : "—";
  const left =
    cursor?.configured !== false &&
    cursor?.tokensRemaining != null &&
    Number.isFinite(cursor.tokensRemaining)
      ? fmtInt(cursor.tokensRemaining)
      : "—";
  return tokenMetricLinesHtml([
    { label: "Today", value: esc(today) },
    { label: opts?.totalLabel ?? "Total (period)", value: esc(fmtInt(periodTotal)) },
    { label: "Left", value: esc(left) },
  ]);
}

function sumAgentTokensSinceCreated(months: PaperAgentStats["months"]): {
  md: number;
  claude: number;
} {
  return months.reduce(
    (acc, bucket) => ({
      md: acc.md + (Number(bucket.mdTokens) || 0),
      claude: acc.claude + (Number(bucket.claudeTokens) || 0),
    }),
    { md: 0, claude: 0 },
  );
}

function rocPerfCls(realized: number | null | undefined, target: number): string {
  if (realized == null || !Number.isFinite(realized)) return "";
  return realized >= target ? "roi-strong" : "roi-loss";
}

function monthLabel(monthKey: string): string {
  const [y, m] = monthKey.split("-").map(Number);
  if (!y || !m) return monthKey;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Analytics+-style month chips + total/monthly KPI cards.
 */
export function paperAgentStatsHtml(
  stats: PaperAgentStats | undefined | null,
  tokenContext?: PaperAgentTokenContext,
): string {
  if (!stats) {
    return `<p class="muted small">Monthly stats will appear after the first save or run.</p>`;
  }

  const cm = stats.currentMonth;
  const selected = stats.selectedMonth;
  const monthChips = stats.months
    .map((b) => {
      let tone = "sfe-month-chip--blue";
      if (b.month < cm) tone = "sfe-month-chip--past";
      else if (b.month === cm) tone = "sfe-month-chip--current";
      const active = b.month === selected ? " active" : "";
      return `<button type="button" class="year-chip sfe-income-month-chip ${tone}${active}" data-paper-action="select-month" data-month="${esc(b.month)}" aria-pressed="${b.month === selected ? "true" : "false"}">${esc(monthLabel(b.month))}</button>`;
    })
    .join("");

  const detail = stats.monthDetail;
  const target = detail.targetRocPct || 0;
  const monthIncome = Number(detail.income ?? detail.realizedPnl) || 0;
  const monthPremiums = Number(detail.premiums ?? detail.premiumCredits) || 0;
  const monthBtc = Number(detail.premiumCorrections ?? detail.btcCost) || 0;
  const monthAssign = Number(detail.assignIncome) || 0;
  const realizedCls = rocPerfCls(detail.rocPct, target);
  const equityCls = detail.equityEom < 0 ? "sfe-neg" : "roi-strong";
  const incomeCls = monthIncome >= 0 ? "roi-strong" : "sfe-neg";
  const assignCls = monthAssign < 0 ? "sfe-neg" : monthAssign > 0 ? "roi-strong" : "";

  const total = stats.total;
  const totalIncome = Number(total.income ?? total.realizedPnl) || 0;
  const totalPremiums = Number(total.premiums ?? 0) || 0;
  const totalBtc = Number(total.premiumCorrections ?? total.btcCost ?? 0) || 0;
  const totalAssign = Number(total.assignIncome ?? 0) || 0;
  const totalCls = (total.rocPct ?? 0) >= 0 ? "roi-strong" : "sfe-neg";
  const totalIncomeCls = totalIncome >= 0 ? "roi-strong" : "sfe-neg";
  const totalAssignCls = totalAssign < 0 ? "sfe-neg" : totalAssign > 0 ? "roi-strong" : "";
  const lifetimeTokens = sumAgentTokensSinceCreated(stats.months);
  const monthMd = Number(detail.mdTokens) || 0;
  const monthClaude = Number(detail.claudeTokens) || 0;
  const throughKey = total.throughMonth || selected;
  const throughLabel = monthLabel(throughKey);
  const todayClaude = stats.todayTokens?.claudeTokens ?? null;

  const totalIncomeDetails = detailsBlock([
    detailLine("Premiums", fmtUsdWhole(totalPremiums), "sfe-income-kpi-premiums"),
    detailLine(
      "BTC adj",
      totalBtc > 0 ? `−${fmtUsdWhole(totalBtc)}` : fmtUsdWhole(0),
      totalBtc > 0 ? "sfe-neg" : "",
    ),
    detailLine("Call Assgn.", fmtUsdWhole(totalAssign), totalAssignCls),
  ]);

  const monthIncomeDetails = detailsBlock([
    detailLine("Premiums", fmtUsdWhole(monthPremiums), "sfe-income-kpi-premiums"),
    detailLine(
      "BTC adj",
      monthBtc > 0 ? `−${fmtUsdWhole(monthBtc)}` : fmtUsdWhole(0),
      monthBtc > 0 ? "sfe-neg" : "",
    ),
    detailLine("Call Assgn.", fmtUsdWhole(monthAssign), assignCls),
    detailLine("Opens", esc(String(detail.opens))),
    detailLine("Closes", esc(String(detail.closes))),
  ]);

  return `
    <div class="sfe-analytics-plus-panel sfe-agents-stats">
      <details class="collapsible sfe-total-performance" data-sfe-collapsible="performance"${collapsibleOpenAttr(AGENTS_COLLAPSIBLE_SCOPE, "performance", true)}>
        <summary class="sfe-total-performance-summary">
          <h3 class="insight-h">Total Performance</h3>
          <span class="hint" tabindex="0" role="img" data-tip="Cumulative through the selected month. Book carries forward — no monthly cash reset.">i</span>
        </summary>
        <div class="sfe-total-performance-body">
          <div class="sfe-inject-kpi-row kpi-row sfe-agents-total-kpi-row">
            <div class="kpi sfe-total-income-kpi">
              <span class="kpi-k">Total Income<span class="hint" tabindex="0" role="img" data-tip="Same accounting as the Analytics tab: STO premiums in the month opened, BTC buybacks in the month closed, call-assignment P/L against the avg cost that applied then. Expiries re-book nothing (premium already counted on STO). Fees are separate. Through the selected month.">i</span></span>
              <span class="sfe-kpi-period sfe-income-kpi-period">through ${esc(throughLabel)}</span>
              <div class="sfe-income-kpi-row">
                <span class="kpi-v ${totalIncomeCls}">${fmtUsdWhole(totalIncome)}</span>
                ${totalIncomeDetails}
              </div>
            </div>
            <div class="kpi">
              <span class="kpi-k">Total Own Capital<span class="hint" tabindex="0" role="img" data-tip="Start + income − fees through the selected month, the same way the Analytics timeline builds own capital. Open contracts are not marked to market and assigned shares stay in the book at cost, so capital only moves on booked income and costs.">i</span></span>
              <span class="sfe-kpi-period sfe-income-kpi-period">through ${esc(throughLabel)} (EOM)</span>
              <div class="sfe-income-kpi-row">
                <span class="kpi-v ${totalCls}">${fmtUsdWhole(total.equity)}</span>
                ${detailsBlock([detailLine("Start", fmtUsdWhole(total.startEquity))])}
              </div>
            </div>
            <div class="kpi">
              <span class="kpi-k">Total ROC</span>
              <span class="sfe-kpi-period sfe-income-kpi-period">through ${esc(throughLabel)}</span>
              <div class="sfe-income-kpi-row">
                <span class="kpi-v ${totalCls}">${fmtPct(total.rocPct)}</span>
                ${detailsBlock([
                  detailLine("Months", esc(String(total.monthsTraded))),
                  detailLine("Basis", "income ÷ start"),
                ])}
              </div>
            </div>
            <div class="kpi">
              <span class="kpi-k">Fees</span>
              <span class="sfe-kpi-period sfe-income-kpi-period">through ${esc(throughLabel)}</span>
              <div class="sfe-income-kpi-row">
                <span class="kpi-v">${fmtUsdWhole(total.fees)}</span>
                ${detailsBlock([
                  detailLine("Opens", esc(String(total.opens))),
                  detailLine("Closes", esc(String(total.closes))),
                ])}
              </div>
            </div>
            <div class="kpi sfe-agents-token-kpi sfe-agents-token-kpi-md sfe-month-card-return-on-capital">
              <span class="kpi-k">MarketData<span class="hint" tabindex="0" role="img" data-tip="Today and Left are the shared app budget (resets daily). Total is this agent’s MarketData usage since agent activation.">i</span></span>
              <span class="sfe-kpi-period sfe-income-kpi-period">since agent activation</span>
              ${marketDataTokenCardHtml(lifetimeTokens.md, tokenContext, { totalLabel: "Total" })}
            </div>
            <div class="kpi sfe-agents-token-kpi sfe-agents-token-kpi-claude sfe-month-card-return-on-capital">
              <span class="kpi-k">Claude<span class="hint" tabindex="0" role="img" data-tip="Today is this agent's Cursor usage today (Pacific). Total is cumulative Cursor usage since agent activation. Left is monthly allowance remaining when configured.">i</span></span>
              <span class="sfe-kpi-period sfe-income-kpi-period">since agent activation</span>
              ${claudeTokenCardHtml(lifetimeTokens.claude, tokenContext, {
                totalLabel: "Total",
                todayClaude,
              })}
            </div>
          </div>
        </div>
      </details>

      <div class="sfe-income-month-detail" data-month="${esc(selected)}">
        <div class="sfe-income-month-selector sfe-agents-month-chips" role="tablist" aria-label="Agent months">
          ${monthChips || `<span class="muted small">No months yet</span>`}
        </div>
        <div class="sfe-month-detail-groups">
          <div class="sfe-month-card-row sfe-month-card-row-shaded">
            <div class="kpi sfe-total-income-kpi sfe-month-card-income sfe-month-card-combined">
              <span class="kpi-k">Income<span class="hint" tabindex="0" role="img" data-tip="Premiums: STO credits in the month opened. BTC adj: buybacks in the month they were paid. Call Assgn.: (strike − share avg cost) × shares in the month assigned. Put assignment and worthless expiry book no option P&amp;L — premium already counted on open.">i</span></span>
              <div class="sfe-income-kpi-row">
                <span class="kpi-v ${incomeCls}">${fmtUsdWhole(monthIncome)}</span>
                ${monthIncomeDetails}
              </div>
              ${renderTargetProgressIndicator(detail.progressPct)}
            </div>
            <div class="sfe-month-card kpi sfe-month-card-own-capital">
              <span class="kpi-k">Own Capital<span class="hint" tabindex="0" role="img" data-tip="Own Capital (EOM) = Base Capital (BOM) + month income − month fees, like the Analytics timeline. Base Capital (BOM) is the prior month’s EOM and the ROC denominator.">i</span></span>
              <div class="sfe-income-kpi-row">
                <span class="kpi-v ${equityCls}">${fmtUsdWhole(detail.equityEom)}</span>
                ${detailsBlock([
                  detailLine("Base (BOM)", fmtUsdWhole(detail.equityBom)),
                  detailLine("Period", "EOM"),
                ])}
              </div>
            </div>
            <div class="sfe-month-card kpi sfe-month-card-return-on-capital">
              <span class="kpi-k">Return On Own Capital<span class="hint" tabindex="0" role="img" data-tip="Target uses expected monthly ROC × Base Capital (BOM). Realized uses month Income (premiums − BTC adj + call assignment P/L) ÷ BOM.">i</span></span>
              <div class="sfe-month-roc-lines">
                <div class="sfe-month-roc-line">
                  <span class="sfe-month-roc-line-k">Target</span>
                  <div class="sfe-month-roc-row">
                    <span class="kpi-v sfe-month-card-static-val">${fmtPct(target)}</span>
                    <span class="kpi-v sfe-month-roc-amt">${detail.targetIncome ? fmtUsdWhole(detail.targetIncome) : "—"}</span>
                  </div>
                </div>
                <div class="sfe-month-roc-line">
                  <span class="sfe-month-roc-line-k">Realized</span>
                  <div class="sfe-month-roc-row">
                    <span class="kpi-v ${realizedCls}">${fmtPct(detail.rocPct)}</span>
                    <span class="kpi-v sfe-month-roc-amt ${realizedCls}">${fmtUsdWhole(monthIncome)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="sfe-month-card kpi sfe-agents-token-kpi sfe-agents-token-kpi-md sfe-month-card-return-on-capital">
              <span class="kpi-k">MarketData<span class="hint" tabindex="0" role="img" data-tip="Shared MarketData budget resets daily at midnight UTC. Total (period) is this agent’s usage in the selected month.">i</span></span>
              ${marketDataTokenCardHtml(monthMd, tokenContext)}
            </div>
            <div class="sfe-month-card kpi sfe-agents-token-kpi sfe-agents-token-kpi-claude sfe-month-card-return-on-capital">
              <span class="kpi-k">Claude<span class="hint" tabindex="0" role="img" data-tip="Today is this agent's Cursor usage today (Pacific). Total (period) is Cursor usage in the selected month. Left is monthly allowance remaining when configured.">i</span></span>
              ${claudeTokenCardHtml(monthClaude, tokenContext, { todayClaude })}
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}
